import { GridEmissionsDto, gridEmissionsPaths } from "@flexidao/dto";
import { ApiClient } from "../api-client";
import {
    hourlyIntensitiesResponseDecoder,
    zonesEmissionsRatesResponseDecoder,
} from "../decoders/grid-emissions";
import { ApiResponse, ApiResultResponse, OverloadApiResponse } from "../types";
import { buildQueries, crash, routeBlocker } from "../utils";

export type GridEmissionsClient = {
    getPublicHourlyIntensities: (params: {
        zones: Array<string>;
        startTime: Date;
        endTime: Date;
        gridEmissionsKey: string;
    }) => ApiResponse<Record<string, Array<GridEmissionsDto.ZoneIntensityDto>>>;
    getPublicYearlyEmissionRatesByYear: (
        year: number,
        zoneIds: Array<string>,
        gridEmissionsKey: string,
    ) => ApiResponse<Array<GridEmissionsDto.EmissionRate>>;
} & ApiClient<gridEmissionsPaths>;

export type OverloadGridEmissionsClient = {
    getPublicHourlyIntensities: (params: {
        zones: Array<string>;
        startTime: Date;
        endTime: Date;
        gridEmissionsKey: string;
    }) => OverloadApiResponse<Record<string, Array<GridEmissionsDto.ZoneIntensityDto>>>;
    getPublicYearlyEmissionRatesByYear: (
        year: number,
        zoneIds: Array<string>,
        gridEmissionsKey: string,
    ) => OverloadApiResponse<Array<GridEmissionsDto.EmissionRate>>;
} & ApiClient<gridEmissionsPaths>;

export type ResultGridEmissionsClient = {
    getPublicHourlyIntensities: (params: {
        zones: Array<string>;
        startTime: Date;
        endTime: Date;
        gridEmissionsKey: string;
    }) => ApiResultResponse<Record<string, Array<GridEmissionsDto.ZoneIntensityDto>>>;
    getPublicYearlyEmissionRatesByYear: (
        year: number,
        zoneIds: Array<string>,
        gridEmissionsKey: string,
    ) => ApiResultResponse<Array<GridEmissionsDto.EmissionRate>>;
} & ApiClient<gridEmissionsPaths>;

export function getGridEmissionsClient(config: Record<string, never>): GridEmissionsClient;
export function getGridEmissionsClient(config: { useResult: boolean }): ResultGridEmissionsClient;
export function getGridEmissionsClient(config: {
    useResult?: boolean;
    useRetries?: boolean;
}): OverloadGridEmissionsClient {
    let url: string = "";

    let useMockData = false;
    if (typeof window !== "undefined") {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        url = window.location.origin;
    } else if (typeof process !== "undefined") {
        useMockData = process.env.MOCK_GRID_EMISSIONS === "true";

        if (!process.env.GRID_EMISSIONS_URL) {
            return crash("Missing GRID_EMISSIONS_URL environment variable.");
        } else {
            url = process.env.GRID_EMISSIONS_URL;
        }
    } else {
        return crash("No window or process, where the hell are we??!");
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    buildQueries({ ...config, useMockData: useMockData }, url);

    const { get } = buildQueries({ ...config, useMockData: useMockData }, url);

    return {
        // Not used in the UI
        getPublicZones: routeBlocker,
        getPublicEnergySourceEmissionFactors: routeBlocker,

        // Used in CFE Diagnostic only
        getPublicHourlyIntensities: ({
            zones,
            startTime,
            endTime,
            gridEmissionsKey,
        }: {
            zones: Array<string>;
            startTime: Date;
            endTime: Date;
            gridEmissionsKey: string;
        }): OverloadApiResponse<Record<string, Array<GridEmissionsDto.ZoneIntensityDto>>> =>
            get({
                path: "/api/grid-emissions/public/hourly-intensities",
                decoder: hourlyIntensitiesResponseDecoder,
                params: {
                    zones,
                    startTime: startTime.toISOString(),
                    endTime: endTime.toISOString(),
                },
                headers: {
                    "x-auth-key": gridEmissionsKey,
                },
            }),

        // Used in CFE Diagnostic only
        getPublicYearlyEmissionRatesByYear: (
            year: number,
            zoneIds: Array<string>,
            gridEmissionsKey: string,
        ): OverloadApiResponse<Array<GridEmissionsDto.EmissionRate>> =>
            get({
                path: `/api/grid-emissions/public/yearly-emission-rates/${year}`,
                decoder: zonesEmissionsRatesResponseDecoder,
                params: {
                    zoneIds,
                },
                headers: {
                    "x-auth-key": gridEmissionsKey,
                },
            }),
    };
}
